import {
  API_V1,
  avatarListResponseSchema,
  nicknameAvailabilityResponseSchema,
  nicknameSuggestionResponseSchema,
  playerProfileSchema,
  type AvatarListResponse,
  type NicknameAvailabilityResponse,
  type PlayerProfile,
} from "@wr/contracts";
import { t } from "@wr/i18n";
import { API_URL, hasApiUrl } from "../env";
import { ApiRequestError } from "./api";

/**
 * The identity endpoints the `/welcome` gate drives (design spec §9).
 *
 * Everything here is a thin, typed call. The rules live on the server, and
 * deliberately so: this module cannot tell a taken nickname from a screened
 * one, because the API refuses to say — same status, same body, same number of
 * suggestions. If a future version of this file *can* tell them apart, that is
 * the bug, not the feature.
 */

async function call(
  path: string,
  init: RequestInit = {},
): Promise<{ status: number; body: unknown }> {
  if (!hasApiUrl()) throw new ApiRequestError(t("error.missingApiUrl"), 0, "unavailable");

  let response: Response;
  try {
    response = await fetch(`${API_URL}${path}`, {
      credentials: "include",
      ...init,
      headers: {
        accept: "application/json",
        ...(init.body ? { "content-type": "application/json" } : {}),
        ...init.headers,
      },
    });
  } catch {
    throw new ApiRequestError(t("error.network"), 0, "unavailable");
  }

  const text = await response.text();
  let body: unknown;
  try {
    body = text ? JSON.parse(text) : undefined;
  } catch {
    body = undefined;
  }
  return { status: response.status, body };
}

function unwrap(status: number, body: unknown): unknown {
  if (status < 400) return body;
  const envelope = body as { error?: { code?: string; message?: string } } | undefined;
  throw new ApiRequestError(
    envelope?.error?.message ?? t("error.unexpected"),
    status,
    envelope?.error?.code,
  );
}

export async function fetchAvatars(): Promise<AvatarListResponse> {
  const { status, body } = await call(`${API_V1}/players/avatars`);
  return avatarListResponseSchema.parse(unwrap(status, body));
}

/**
 * Debounced availability check (design spec §9, 400ms).
 *
 * The response for a taken name and for a screened name is byte-identical, so
 * the screen renders one state for both — `welcome.taken` and
 * `welcome.unavailable` are the same string for exactly this reason.
 */
export async function checkNickname(nickname: string): Promise<NicknameAvailabilityResponse> {
  const { status, body } = await call(
    `${API_V1}/players/nickname-available?nickname=${encodeURIComponent(nickname)}`,
  );
  return nicknameAvailabilityResponseSchema.parse(unwrap(status, body));
}

/** A fresh generated nickname. Does NOT persist — Continue is what commits. */
export async function rerollNickname(): Promise<string> {
  const { status, body } = await call(`${API_V1}/players/me/nickname/reroll`, {
    method: "POST",
    body: "{}",
  });
  return nicknameSuggestionResponseSchema.parse(unwrap(status, body)).nickname;
}

export async function setNickname(nickname: string): Promise<PlayerProfile> {
  const { status, body } = await call(`${API_V1}/players/me/nickname`, {
    method: "POST",
    body: JSON.stringify({ nickname }),
  });
  return playerProfileSchema.parse(unwrap(status, body));
}

export async function setAvatar(avatarId: string): Promise<PlayerProfile> {
  const { status, body } = await call(`${API_V1}/players/me/avatar`, {
    method: "POST",
    body: JSON.stringify({ avatarId }),
  });
  return playerProfileSchema.parse(unwrap(status, body));
}

/**
 * Closes the mandatory identity gate (PRD §4.4).
 *
 * Called by guests too: a guest cannot change their nickname or avatar, but a
 * guest does press Continue, and the gate has to record that or they land back
 * on it every time the splash routes.
 */
export async function confirmIdentity(): Promise<PlayerProfile> {
  const { status, body } = await call(`${API_V1}/players/me/identity/confirm`, {
    method: "POST",
    body: "{}",
  });
  return playerProfileSchema.parse(unwrap(status, body));
}

export interface ApiVersion {
  commit: string;
  env: string;
}

/**
 * `GET /api/v1/version` — the api's own build SHA, for the splash footer's
 * second labelled value (design spec §5 / S1 QA finding N7).
 *
 * Resolves to `null` rather than throwing: a missing api SHA must degrade to
 * an em-dash in a footer, never to a failed boot.
 */
export async function fetchApiVersion(): Promise<ApiVersion | null> {
  try {
    const { status, body } = await call(`${API_V1}/version`);
    if (status >= 400) return null;
    const parsed = body as { commit?: unknown; env?: unknown };
    return {
      commit: typeof parsed.commit === "string" ? parsed.commit : "—",
      env: typeof parsed.env === "string" ? parsed.env : "",
    };
  } catch {
    return null;
  }
}
